// unifiedApi.ts
import axios, { AxiosRequestConfig } from 'axios';
import { ENV } from '../../config/env';
import { useAuthStore } from 'store/useAuthStore';

export const apiClient = axios.create({
  baseURL: ENV.apiBaseUrl,
  // timeout: 15000,
});

const ERROR_MESSAGES: Record<number, string> = {
  412: 'Session expired. Please login again.',
  403: 'Forbidden request.',
  404: 'The requested resource was not found.',
  500: 'Internal server error. Please try again later.',
  502: 'Bad gateway. Please try again later.',
  111: 'Unable to connect to the server. Please try after some time.',
  408: 'Request timed out. Please try again.',
  429: 'Too many requests. Please wait and try again.',
  503: 'Service unavailable. Please try again later.',
  504: 'Server taking too long to respond. Please try again later.',
};

const getErrorMessage = (error: any) => {
  const status = error?.status || error.response?.status;

  if (error.code === 'ECONNABORTED') return 'Request timeout.';
  if (error.message === 'Network Error') return 'No internet connection.';
  if (status && ERROR_MESSAGES[status]) return ERROR_MESSAGES[status];

  return (
    error.response?.data?.message ||
    error.response?.data?.other_message ||
    error.response?.data?.error ||
    'Something went wrong'
  );
};

apiClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? useAuthStore.getState().token : null;

  if (token) config.headers['X-Session'] = `${token}`;

  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const errorMessage = getErrorMessage(error);
    if (error?.response?.status === 401) {
      useAuthStore.getState().handleSessionExpiry();
      return Promise.reject();
    } else return Promise.reject(errorMessage);
  }
);

export const axiosRequest = async <T>(
  config: AxiosRequestConfig,
  set?: (state: any) => void,
  options?: {
    setLoading?: boolean;
    loadingKey?: string;
  }
): Promise<T> => {
  const loadingKey = options?.loadingKey;

  if (set) {
    if (loadingKey) {
      set((state: any) => ({
        loading: {
          ...state.loading,
          [loadingKey]: true,
        },
      }));
    } else {
      set({ loading: true });
    }
  }

  try {
    const response = await apiClient(config);

    if (set) {
      if (loadingKey) {
        set((state: any) => ({
          loading: {
            ...state.loading,
            [loadingKey]: false,
          },
          error: null,
        }));
      } else {
        set({ loading: false, error: null });
      }
    }

    if (config.responseType === 'blob') {
      return response?.data as T;
    }
    return response?.data?.message || (response?.data as T);
  } catch (error: any) {
    if (set) {
      if (loadingKey) {
        set((state: any) => ({
          loading: {
            ...state.loading,
            [loadingKey]: false,
          },
          error,
        }));
      } else {
        set({ loading: false, error });
      }
    }

    throw error;
  }
};
